import { CurrencyInput } from '../../../../components/base/CurrencyInput';
import { Plan } from '../types';

interface Props {
  form: Omit<Plan, 'id'>;
  set: (f: Partial<Omit<Plan, 'id'>>) => void;
}

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[14px] outline-none focus:border-violet-500 transition-colors';

const Toggle = ({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) => (
  <button type="button" onClick={() => onChange(!value)} className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0 overflow-hidden ${value ? 'bg-violet-600' : 'bg-white/10'}`}>
    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${value ? 'left-6' : 'left-1'}`} />
  </button>
);

const PlanEditorPricing = ({ form, set }: Props) => {
  const annualSaving =
    form.price_monthly > 0 && form.price_annual > 0 ? form.price_monthly * 12 - form.price_annual : 0;

  const autoDiscount =
    form.price_monthly > 0 && form.price_annual > 0
      ? Math.round((1 - form.price_annual / (form.price_monthly * 12)) * 100)
      : 0;

  return (
    <div>
      {/* Monthly/Annual pricing */}
      <div className="bg-white/3 border border-white/8 rounded-2xl p-5 mb-5">
        <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-4">Preços Mensal e Anual</p>
        <p className="text-[12px] text-white/40 mb-4">Configure preços separados para cobrança mensal e anual. O desconto anual será calculado automaticamente.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[12px] text-white/50 mb-1.5">Preço mensal (BRL)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-white/40 z-10 pointer-events-none">R$</span>
              <CurrencyInput
                value={form.price_monthly}
                onChange={(v) => set({ price_monthly: v, price_brl: v })}
                placeholder="0,00"
                className={`${inputCls} pl-9`}
              />
            </div>
          </div>
          <div>
            <label className="block text-[12px] text-white/50 mb-1.5">Preço anual (BRL total)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-white/40 z-10 pointer-events-none">R$</span>
              <CurrencyInput
                value={form.price_annual}
                onChange={(v) => set({ price_annual: v })}
                placeholder="0,00"
                className={`${inputCls} pl-9`}
              />
            </div>
          </div>
        </div>

        {annualSaving > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center">
              <p className="text-[10px] text-emerald-400/70 mb-1">Desconto calculado</p>
              <p className="text-[20px] font-700 text-emerald-400">{autoDiscount}%</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
              <p className="text-[10px] text-white/40 mb-1">Economia anual</p>
              <p className="text-[18px] font-700 text-white">
                {annualSaving.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
              <p className="text-[10px] text-white/40 mb-1">Preço/mês anual</p>
              <p className="text-[18px] font-700 text-white">
                {(form.price_annual / 12).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
            </div>
          </div>
        )}

        <div className="mt-4">
          <label className="block text-[12px] text-white/50 mb-1.5">
            Desconto anual exibido ao usuário (%)
            <span className="ml-2 text-white/30 text-[11px]">— preenchido automaticamente, pode ajustar manualmente</span>
          </label>
          <input
            type="number" min="0" max="100"
            value={form.annual_discount_percent || autoDiscount}
            onChange={(e) => set({ annual_discount_percent: Number(e.target.value) })}
            className={inputCls}
          />
        </div>
      </div>

      {/* Legacy single price */}
      <div className="bg-white/3 border border-white/8 rounded-2xl p-5 mb-5">
        <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-3">Preço Legado (campo único)</p>
        <p className="text-[12px] text-white/40 mb-3">Usado se o plano não tiver preços separados por periodicidade.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[12px] text-white/50 mb-1.5">Valor (BRL)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-white/40 z-10 pointer-events-none">R$</span>
              <CurrencyInput
                value={form.price_brl}
                onChange={(v) => set({ price_brl: v })}
                placeholder="0,00"
                className={`${inputCls} pl-9`}
              />
            </div>
          </div>
          <div>
            <label className="block text-[12px] text-white/50 mb-1.5">Periodicidade</label>
            <select value={form.periodicity} onChange={(e) => set({ periodicity: e.target.value })} className={`${inputCls} cursor-pointer`}>
              <option value="monthly" className="bg-[#1A1A2E]">Mensal</option>
              <option value="annual" className="bg-[#1A1A2E]">Anual</option>
              <option value="lifetime" className="bg-[#1A1A2E]">Vitalício</option>
            </select>
          </div>
        </div>
      </div>

      {/* Trial */}
      <div className="bg-white/3 border border-white/8 rounded-2xl p-5">
        <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-3">Período de Trial</p>
        <div className="flex items-center justify-between py-2 mb-4">
          <span className="text-[13px] text-white/70">Trial disponível para novos usuários</span>
          <Toggle value={form.trial_available} onChange={(v) => set({ trial_available: v })} />
        </div>
        {form.trial_available && (
          <div>
            <label className="block text-[12px] text-white/50 mb-1.5">Duração do trial (dias)</label>
            <input
              type="number" min={1}
              value={form.trial_days}
              onChange={(e) => set({ trial_days: Number(e.target.value) })}
              className={inputCls}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanEditorPricing;
